// src/comps/MyMenu.js
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { Link, useNavigate } from "react-router-dom";

function MyMenu() {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const offcanvasRef = useRef(null);
  const navigate = useNavigate();

  const userData = JSON.parse(sessionStorage.getItem("ldata"));
  const roleId = userData?.role_id;
  const userType = userData?.type;
  const userId = userData?.user_id;

  const isAdmin = roleId === 1 || roleId === "1";

  // Initialize offcanvas once when component mounts
  useEffect(() => {
    let mounted = true;
    const initOffcanvas = () => {
      const offcanvasEl = document.getElementById("mobileOffcanvas");
      if (!offcanvasEl || !mounted) return;

      try {
        if (typeof window.bootstrap === "undefined") {
          console.warn("Bootstrap not loaded yet, retrying...");
          setTimeout(initOffcanvas, 100);
          return;
        }

        let offcanvasInstance =
          window.bootstrap.Offcanvas.getInstance(offcanvasEl);
        if (!offcanvasInstance) {
          offcanvasInstance = new window.bootstrap.Offcanvas(offcanvasEl, {
            backdrop: true,
            keyboard: true,
            scroll: false,
          });
        }
        offcanvasRef.current = offcanvasInstance;

        const handleHidden = () => {
          document
            .querySelectorAll(".offcanvas-backdrop, .modal-backdrop")
            .forEach((b) => b.remove());
          document.body.classList.remove("offcanvas-open", "modal-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        };

        offcanvasEl.addEventListener("hidden.bs.offcanvas", handleHidden);

        const handleDocPointer = (e) => {
          const el = document.getElementById("mobileOffcanvas");
          if (!el || !el.classList.contains("show")) return;

          if (
            e.target &&
            e.target.classList &&
            (e.target.classList.contains("offcanvas-backdrop") ||
              e.target.classList.contains("modal-backdrop"))
          ) {
            try {
              offcanvasRef.current?.hide();
            } catch (err) {
              /* fallback below */
            }
            return;
          }

          if (!e.target.closest || !e.target.closest("#mobileOffcanvas")) {
            try {
              offcanvasRef.current?.hide();
            } catch (err) {
              el.classList.remove("show");
              document
                .querySelectorAll(".offcanvas-backdrop, .modal-backdrop")
                .forEach((b) => b.remove());
              document.body.classList.remove("offcanvas-open", "modal-open");
              document.body.style.overflow = "";
              document.body.style.paddingRight = "";
            }
          }
        };

        document.addEventListener("click", handleDocPointer, true);
        document.addEventListener("touchstart", handleDocPointer, true);

        return () => {
          offcanvasEl.removeEventListener("hidden.bs.offcanvas", handleHidden);
          document.removeEventListener("click", handleDocPointer, true);
          document.removeEventListener("touchstart", handleDocPointer, true);
        };
      } catch (err) {
        console.error("Bootstrap Offcanvas initialization failed:", err);
      }
    };

    const timer = setTimeout(initOffcanvas, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
      const offcanvasEl = document.getElementById("mobileOffcanvas");
      if (offcanvasEl) {
        document
          .querySelectorAll(".offcanvas-backdrop, .modal-backdrop")
          .forEach((b) => b.remove());
        document.body.classList.remove("offcanvas-open", "modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };
  }, []);

  const closeMobileOffcanvas = () => {
    if (offcanvasRef.current) {
      try {
        offcanvasRef.current.hide();
        return;
      } catch (err) {
        console.error("Error hiding offcanvas:", err);
      }
    }
    const offcanvasEl = document.getElementById("mobileOffcanvas");
    if (offcanvasEl) {
      offcanvasEl.classList.remove("show");
      document
        .querySelectorAll(".offcanvas-backdrop, .modal-backdrop")
        .forEach((b) => b.remove());
      document.body.classList.remove("offcanvas-open", "modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
  };

  const fetchRoleModules = async () => {
    try {
      if (isAdmin) {
        const resAll = await axios.get(
          AppConst.BASE_URL + AppConst.GetAllModules
        );
        if (resAll.data.status) {
          const grouped = resAll.data.list.reduce((acc, m) => {
            acc[m.module_name] = acc[m.module_name] || [];
            acc[m.module_name].push(m);
            return acc;
          }, {});
          setModules(grouped);
        }
        return;
      }

      if (!roleId) {
        console.warn("Role ID missing for user");
        setModules({});
        return;
      }

      const res = await axios.get(
        AppConst.BASE_URL + AppConst.GetModuleAssign,
        { params: { role_id: roleId } }
      );

      if (!res.data.status || !res.data.list.length) {
        setModules({});
        return;
      }

      const allowedModules = res.data.list;

      const resAll = await axios.get(
        AppConst.BASE_URL + AppConst.GetAllModules
      );

      if (resAll.data.status) {
        const filtered = resAll.data.list.filter((m) =>
          allowedModules.includes(m.id)
        );

        const grouped = filtered.reduce((acc, m) => {
          acc[m.module_name] = acc[m.module_name] || [];
          acc[m.module_name].push(m);
          return acc;
        }, {});

        setModules(grouped);
      }
    } catch (err) {
      console.error("Error loading modules", err);
      setModules({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, userType, userId]);

  useEffect(() => {
    const applyDesktopClasses = () => {
      if (window.innerWidth >= 992) {
        if (isExpanded) {
          document.body.classList.add("sidebar-expanded");
          document.body.classList.remove("sidebar-collapsed");
        } else {
          document.body.classList.add("sidebar-collapsed");
          document.body.classList.remove("sidebar-expanded");
        }
      } else {
        document.body.classList.remove("sidebar-expanded", "sidebar-collapsed");
      }
    };

    applyDesktopClasses();
    window.addEventListener("resize", applyDesktopClasses);
    return () => {
      window.removeEventListener("resize", applyDesktopClasses);
      document.body.classList.remove("sidebar-expanded", "sidebar-collapsed");
    };
  }, [isExpanded]);

  useEffect(() => {
    if (window.innerWidth >= 992) {
      document.body.classList.add("sidebar-collapsed");
    }
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const MenuList = ({ onLinkClick }) => (
    <ul className="navbar-nav" id="navbar-nav">
      <li className="menu-title">
        <span className="menu-text">Menu</span>
      </li>

      {isAdmin && (
        <li className="nav-item">
          <Link className="nav-link" to="/" onClick={onLinkClick}>
            <i className="ri-home-4-line menu-icon"></i>
            <span className="menu-text">Home</span>
          </Link>
        </li>
      )}

      {Object.keys(modules).map((group, idx) => {
        const filteredGroup = modules[group].filter(
          (sub) =>
            sub?.submodule_name?.toLowerCase() !== "home" &&
            sub?.slug?.toLowerCase() !== "home"
        );

        if (!filteredGroup.length) return null;

        return (
          <li className="nav-item" key={idx}>
            {/* ✅ No duplicate id, no data-bs-toggle */}
            {/* ✅ Uses nextElementSibling to find THIS instance's collapse div */}
            <a
              className="nav-link menu-link"
              href="#!"
              role="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                // ✅ Find the sibling collapse div relative to the clicked <a>
                const clickedLink = e.currentTarget;
                const target = clickedLink.nextElementSibling;

                if (target && target.classList.contains("menu-dropdown")) {
                  if (window.bootstrap && window.bootstrap.Collapse) {
                    const bsCollapse =
                      window.bootstrap.Collapse.getOrCreateInstance(target, {
                        toggle: false,
                      });
                    bsCollapse.toggle();
                  } else {
                    target.classList.toggle("show");
                  }
                }
              }}
            >
              <i
                className={`ri ${filteredGroup[0].icon_name} menu-icon`}
              ></i>
              <span className="menu-text">{group}</span>
            </a>

            {/* ✅ No id attribute needed — found via nextElementSibling */}
            <div className="collapse menu-dropdown">
              <ul className="nav nav-sm flex-column">
                {filteredGroup.map((sub) => (
                  <li className="nav-item" key={sub.id}>
                    <Link
                      to={`/${sub.slug}`}
                      className="nav-link"
                      onClick={onLinkClick}
                    >
                      {sub.submodule_name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </li>
        );
      })}

      <li className="nav-item">
        <Link
          className="nav-link"
          to="/change_password"
          onClick={onLinkClick}
        >
          <i className="ri-lock-password-line menu-icon"></i>
          <span className="menu-text">Change Password</span>
        </Link>
      </li>

      <li className="nav-item">
        <a
          href="#!"
          className="nav-link menu-link"
          onClick={(e) => {
            e.preventDefault();
            sessionStorage.removeItem("ldata");
            navigate("/login", { replace: true });
          }}
        >
          <i className="ri-logout-box-r-line menu-icon"></i>
          <span className="menu-text">Logout</span>
        </a>
      </li>
    </ul>
  );

  const openMobileOffcanvas = () => {
    if (offcanvasRef.current) {
      try {
        offcanvasRef.current.show();
      } catch (err) {
        console.error("Error showing offcanvas:", err);
      }
    } else {
      const offcanvasEl = document.getElementById("mobileOffcanvas");
      if (offcanvasEl && window.bootstrap) {
        try {
          const instance =
            window.bootstrap.Offcanvas.getInstance(offcanvasEl) ||
            new window.bootstrap.Offcanvas(offcanvasEl);
          offcanvasRef.current = instance;
          instance.show();
        } catch (err) {
          console.error("Error creating/showing offcanvas:", err);
        }
      }
    }
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className={`app-menu navbar-menu compact-sidebar d-none d-lg-block ${
          isExpanded ? "expanded" : ""
        }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <div className="navbar-brand-box">
          <Link to="/" className="logo logo-dark">
            <span className="logo-sm">
              <img src="assets/images/logo-sm.png" alt="" height="42" />
            </span>
            <span className="logo-lg">
              <img src="assets/images/logo-dark.png" alt="" height="37" />
            </span>
          </Link>
        </div>

        <div id="scrollbar45">
          <div className="container-fluid">
            <MenuList onLinkClick={() => {}} />
          </div>
        </div>

        <div className="sidebar-background"></div>
      </div>

      {/* Mobile offcanvas */}
      <div className="d-lg-none">
        <div
          className="offcanvas offcanvas-start"
          tabIndex="-1"
          id="mobileOffcanvas"
          aria-labelledby="mobileOffcanvasLabel"
          data-bs-backdrop="true"
          data-bs-scroll="false"
          data-bs-keyboard="true"
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="mobileOffcanvasLabel">
              Menu
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={closeMobileOffcanvas}
            />
          </div>

          <div className="offcanvas-body">
            <div className="container-fluid">
              <MenuList onLinkClick={closeMobileOffcanvas} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MyMenu;